import Theme from '@kyruus/ui-theme';
import { scrollToTopCCF } from './third_party/cerner';

/**
 * Returns a function that restores the page's current scroll position. Only
 * PMC's own document is restored, so when PMC is embedded in an iframe the
 * parent page is left where it is. The body is included in case it, rather
 * than the document, is the scroll container.
 */
const saveScrollPosition = (): (() => void) => {
  const { body, documentElement } = window.document;
  const scrollingElement = window.document.scrollingElement ?? documentElement;
  const elements =
    scrollingElement === body ? [body] : [scrollingElement, body];
  const positions = elements.map((element) => ({
    element,
    top: element.scrollTop,
    left: element.scrollLeft
  }));

  return () => {
    positions.forEach(({ element, top, left }) => {
      element.scrollTop = top;
      element.scrollLeft = left;
    });
  };
};

/**
 * On mobile, scrolls the browser so the top of an opening drawer is visible.
 * Fixed-position drawers can open offscreen when PMC is embedded in an iframe.
 * @param drawerId the id of the drawer, which MUI applies to the drawer root
 * @returns a function that restores the page's previous scroll position
 */
export const scrollDrawerIntoViewOnMobile = (
  drawerId: string
): (() => void) | undefined => {
  const isScreenMediumOrAbove = () =>
    window.matchMedia?.(`(min-width:${Theme.screen_medium})`).matches;

  if (isScreenMediumOrAbove()) {
    return;
  }

  const restoreScrollPosition = saveScrollPosition();

  // wait for the drawer to render into its portal before scrolling to it
  const timeout = setTimeout(() => {
    const paper = window.document
      .getElementById(drawerId)
      ?.querySelector<HTMLElement>('.MuiDrawer-paper');
    if (paper) {
      window.scrollTo(
        window.scrollX,
        paper.getBoundingClientRect().top + window.scrollY
      );
    }
    scrollToTopCCF();
  });

  return () => {
    clearTimeout(timeout);
    // FYI this doesn't work in mobile safari when in an iframe
    restoreScrollPosition();
  };
};
